/* ==========================================================================
   Jane Lushka v1.2, phase 4: quick add everywhere (K11, K12, K13, K26; collection:M2, cart:4b, cart:M3).
   Loaded with defer on every page by layout/theme.liquid. Plain ES5, no dependencies.

   1) The ONE size sheet of the page (snippets/jl-size-sheet.liquid, div.jl-qa-sheet at body level). Any
      [data-jl-qa-open] button opens it (delegated click): the card "+", the bag and product page "Shop the look"
      rows, the empty-bag picks, Recently viewed and Wishlist cards. Rows come from the product's payload
      (snippets/jl-size-data: inline next to the trigger, else Section Rendering ?section_id=jl-size-data, cached
      per locale root and handle). A product without a size list ("one") is added at once, no sheet.
      Open: rows built, page inert (phase 3 algorithm), focus synchronously on the first available row, then the
      slide. Tab stays inside; Escape closes on keydown and swallows the matching keyup (the bag closes on keyup);
      backdrop, close button and a drag on the handle close; crossing 768px closes; the phase 3 product page sheet
      or the collection filter sheet opening closes it. From 768px a 480px panel at the bottom, right aligned over
      the bag when the opener is inside cart-drawer (.jl-qa-sheet--drawer), centred otherwise (CSS).
   2) The add path of every phase 4 add (K12): POST routes.cart_add_url with the drawer's sections, then
      drawer.renderContents(state) and drawer.open() (phase 2 counts it as trigger 'add'). No cart-drawer on the
      page (/cart): to the cart page. A 422 keeps the sheet open, marks the row sold out and shows Shopify's
      description (or add_error). Prices are never formatted here: every price string comes from Liquid.
   3) Remembered size (K26): localStorage jl:size:v1, written after a successful add of a size and when a size is
      chosen on the product page (phase 3 jl:pdp-state); read by "In stock in my size". Storage blocked: no memory.
   4) Bag fallback rows (cart:4b): sections/cart-drawer.liquid prints [data-jl-bag-look-fallback] when no Shop
      the look piece qualifies; while the bag is open it is filled from Shopify's related products
      (sections/jl-bag-look.liquid, headed "You may also like", not "Shop the look"), one request per locale root
      and product id per page life (a failed request is tried again by the next fill), at most 2 rows.
   5) Empty-bag picks (cart:M3): sections/cart-drawer.liquid prints [data-jl-bag-picks] only while the bag is
      empty, with the images in data-jl-src / data-jl-srcset; they get their sources when the bag opens. A bag
      emptied on the page (cart.js re-renders the lines, never the empty block) gets the picks from one
      ?sections=cart-drawer request per page life (kept for a later empty bag), inserted after the empty-bag
      button.

   API: window.JLSizeSheet { __v, open, close, isOpen, getData, prefetch, add, rememberSize, rememberedSize }.
   Events on document (bubbling): jl:size-sheet-open { source, product_id, item_name }, jl:size-sheet-close
   { source, reason }, jl:quick-add { source, product_id, variant_id, size, item_name, item_brand, value, currency },
   jl:quick-add-error { source, variant_id, size, status, message }. GA4: snippets/ga4-custom-events.liquid.
   ========================================================================== */
(function () {
  'use strict';

  if (window.JLSizeSheet && window.JLSizeSheet.__v) return;

  var SIZE_KEY = 'jl:size:v1';
  var SKIP_INERT = /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/;
  var ONE_SIZE = /^(one ?size|os|onesize|one size fits all|einheitsgr(ö|oe|o)(ss|ß)e|(éé|ee)n ?maat|taille unique|taglia unica|en ?st(ø|o)rrelse|en ?storlek)$/i;
  var LOOK_ROWS_MAX = 2;

  var mqDesktop = window.matchMedia ? window.matchMedia('(min-width: 768px)') : null;
  var dataCache = {};      // locale root + handle -> Promise<payload or null>
  var relatedCache = {};   // locale root + product id -> Promise<block HTML, null (no block) or false (failed)>
  var picksReq = null;     // Promise<picks HTML, '' (none) or false (failed)> of the one ?sections=cart-drawer request
  var openSeq = 0;
  var swallowOff = null;

  var st = {
    open: false,
    data: null,
    source: '',
    opener: null,
    inDrawer: false,
    busy: false,
    inerted: []
  };

  /* ---- helpers ---------------------------------------------------------- */
  function sheetEl() { return document.querySelector('[data-jl-qa-sheet]'); }
  function part(sel) { var s = sheetEl(); return s ? s.querySelector(sel) : null; }
  function panelEl() { return part('.jl-qa-sheet__panel'); }
  function drawerHost() { return document.querySelector('cart-drawer'); }
  function bagOpen() { return document.body.classList.contains('cart-drawer-is-open'); }

  function root() {
    var s = sheetEl();
    var r = (s && s.getAttribute('data-root')) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function cssEsc(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function dispatch(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function visible(el) {
    return !!(el && document.documentElement.contains(el) && el.getClientRects().length);
  }

  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function inSheet(el) {
    return !!(el && el.closest && el.closest('[data-jl-qa-sheet]'));
  }

  function str(data, key, attr) {
    var s = data && data.s && data.s[key];
    if (s) return s;
    var sheet = sheetEl();
    return (sheet && attr && sheet.getAttribute(attr)) || '';
  }

  function currency(data) {
    if (data && data.cur) return data.cur;
    var c = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return c || '';
  }

  /* ---- 3) remembered size ------------------------------------------------- */
  function rememberSize(size) {
    var v = String(size == null ? '' : size).replace(/^\s+|\s+$/g, '');
    if (!v || v.indexOf(' / ') !== -1 || ONE_SIZE.test(v)) return;
    try { window.localStorage.setItem(SIZE_KEY, v); } catch (e) { /* storage blocked: no size memory */ }
  }

  function rememberedSize() {
    try { return window.localStorage.getItem(SIZE_KEY) || ''; } catch (e) { return ''; }
  }

  /* ---- payload ------------------------------------------------------------ */
  function parsePayload(script) {
    var d = null;
    try { d = JSON.parse(script.textContent || ''); } catch (e) { d = null; }
    return valid(d) ? d : null;
  }

  function valid(d) {
    return !!(d && typeof d === 'object' && d.sizes && d.sizes.length && d.id);
  }

  function scopeOf(el) {
    if (!el || !el.closest) return null;
    return el.closest('[data-jl-look-row], [data-jl-card], .product-card, [data-jl-bag-picks] li');
  }

  // inline payload: the trigger's own row or card first, then the whole document
  function findInline(trigger, id, handle) {
    var scopes = [];
    var own = scopeOf(trigger);
    if (own) scopes.push(own);
    scopes.push(document);
    for (var i = 0; i < scopes.length; i++) {
      var scope = scopes[i];
      if (id) {
        var s = scope.querySelector('script[data-jl-size-data="' + cssEsc(id) + '"]');
        var d = s ? parsePayload(s) : null;
        if (d) return d;
      } else if (handle) {
        var all = scope.querySelectorAll('script[data-jl-size-data]');
        for (var j = 0; j < all.length; j++) {
          var p = parsePayload(all[j]);
          if (p && p.handle === handle) return p;
        }
      }
    }
    return null;
  }

  // the payload that belongs to an element inside a row or card (strip buttons, "+")
  function payloadNear(el) {
    var scope = scopeOf(el);
    var s = scope ? scope.querySelector('script[data-jl-size-data]') : null;
    return s ? parsePayload(s) : null;
  }

  function fetchData(handle) {
    var key = root() + '|' + handle;
    if (dataCache[key]) return dataCache[key];
    var url = root() + 'products/' + encodeURIComponent(handle) + '?section_id=jl-size-data';
    var p = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var s = doc.querySelector('script[data-jl-size-data]');
        return s ? parsePayload(s) : null;
      })
      .catch(function () { return null; });
    dataCache[key] = p;
    // a failed request may be tried again by the next tap
    p.then(function (d) { if (!d && dataCache[key] === p) delete dataCache[key]; });
    return p;
  }

  function idHandle(trigger, key) {
    var id = '';
    var handle = '';
    if (trigger && trigger.getAttribute) {
      id = trigger.getAttribute('data-product-id') || '';
      handle = trigger.getAttribute('data-handle') || '';
    }
    if (key != null && key !== '') {
      if (/^\d+$/.test(String(key))) { id = String(key); } else { handle = String(key); id = ''; }
    }
    return { id: id, handle: handle };
  }

  function getData(trigger, key) {
    var ih = idHandle(trigger, key);
    var inline = findInline(trigger, ih.id, ih.handle);
    if (inline) return Promise.resolve(inline);
    if (!ih.handle) return Promise.resolve(null);
    return fetchData(ih.handle);
  }

  function prefetch(handle) {
    if (handle) fetchData(String(handle));
  }

  /* ---- sheet: rows ---------------------------------------------------------- */
  function render(data) {
    var title = part('#jl-qa-sheet-title');
    if (title) title.textContent = str(data, 'title') || title.textContent;
    var name = part('[data-jl-qa-name]');
    if (name) name.textContent = data.title || '';
    var price = part('[data-jl-qa-price]');
    if (price) price.textContent = data.price || '';
    var thumb = part('.jl-qa-sheet__thumb');
    if (thumb) {
      if (data.img) {
        thumb.src = data.img;
        thumb.hidden = false;
      } else {
        thumb.removeAttribute('src');
        thumb.hidden = true;
      }
    }
    var close = part('.jl-qa-sheet__close');
    if (close && data.s && data.s.close) close.setAttribute('aria-label', data.s.close);

    var list = part('[data-jl-qa-list]');
    if (!list) return;
    list.textContent = '';
    for (var i = 0; i < data.sizes.length; i++) {
      var sz = data.sizes[i] || {};
      var sold = !sz.a || !sz.id;
      var hintText = sz.h || (sold ? str(data, 'sold') : '');
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'jl-qa-sheet__option' + (sold ? ' is-sold-out' : (sz.h ? ' is-low' : ''));
      b.setAttribute('data-variant-id', String(sz.id || 0));
      b.setAttribute('data-value', String(sz.v == null ? '' : sz.v));
      b.setAttribute('aria-disabled', sold ? 'true' : 'false');
      var v = document.createElement('span');
      v.className = 'jl-qa-sheet__value';
      v.textContent = String(sz.v == null ? '' : sz.v);
      b.appendChild(v);
      if (hintText) {
        var h = document.createElement('span');
        h.className = 'jl-qa-sheet__hint';
        var sep = document.createElement('span');
        sep.className = 'visually-hidden';
        sep.textContent = ', ';
        h.appendChild(sep);
        h.appendChild(document.createTextNode(hintText));
        b.appendChild(h);
      }
      li.appendChild(b);
      list.appendChild(li);
    }
    list.scrollTop = 0;
  }

  function markSoldOut(opt, data) {
    if (!opt) return;
    opt.classList.remove('is-low');
    opt.classList.add('is-sold-out');
    opt.setAttribute('aria-disabled', 'true');
    var h = opt.querySelector('.jl-qa-sheet__hint');
    var sold = str(data, 'sold');
    if (!sold) return;
    if (!h) {
      h = document.createElement('span');
      h.className = 'jl-qa-sheet__hint';
      opt.appendChild(h);
    }
    h.textContent = '';
    var sep = document.createElement('span');
    sep.className = 'visually-hidden';
    sep.textContent = ', ';
    h.appendChild(sep);
    h.appendChild(document.createTextNode(sold));
  }

  function showError(text) {
    var el = part('.jl-qa-sheet__error');
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  function setStatus(text) {
    var el = part('[data-jl-qa-status]');
    if (el) el.textContent = text || '';
  }

  /* ---- sheet: focus, inert, keys ------------------------------------------ */
  function focusables() {
    var panel = panelEl();
    if (!panel) return [];
    var list = panel.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < list.length; i++) if (visible(list[i])) out.push(list[i]);
    return out;
  }

  // while the sheet is open every sibling up the ancestor chain is inert (screen reader swipes stay in the
  // dialog); only what this set is released again
  function inertPage(on) {
    var i;
    if (!on) {
      for (i = 0; i < st.inerted.length; i++) st.inerted[i].removeAttribute('inert');
      st.inerted = [];
      return;
    }
    if (st.inerted.length) return;
    var list = [];
    for (var el = sheetEl(); el && el.parentElement && el !== document.body; el = el.parentElement) {
      var kids = el.parentElement.children;
      for (i = 0; i < kids.length; i++) {
        var k = kids[i];
        if (k === el || SKIP_INERT.test(k.tagName) || k.hasAttribute('inert')) continue;
        k.setAttribute('inert', '');
        list.push(k);
      }
    }
    st.inerted = list;
  }

  // the bag closes on the keyup of Escape (cart-drawer.js): the keyup of the press that closed the sheet must not
  // reach it once focus is back in the bag
  function swallowKeyup() {
    if (swallowOff) swallowOff();
    var timer = 0;
    function off() {
      window.removeEventListener('keyup', onUp, true);
      clearTimeout(timer);
      if (swallowOff === off) swallowOff = null;
    }
    function onUp(e) {
      if (e.key === 'Escape' || e.key === 'Esc') e.stopPropagation();
      off();
    }
    window.addEventListener('keyup', onUp, true);
    timer = setTimeout(off, 1000);
    swallowOff = off;
  }

  function onKeydown(e) {
    if (!st.open) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      e.stopPropagation();
      swallowKeyup();
      close({ reason: 'escape' });
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables();
    var panel = panelEl();
    if (!f.length || !panel) { e.preventDefault(); return; }
    var first = f[0];
    var last = f[f.length - 1];
    var a = document.activeElement;
    if (e.shiftKey) {
      if (a === first || !panel.contains(a)) { e.preventDefault(); focusEl(last); }
    } else if (a === last || !panel.contains(a)) {
      e.preventDefault();
      focusEl(first);
    }
  }

  // cookie banner: the bag's inset (assets/jl-cart-drawer.js) also lifts the panel
  function syncInset() {
    var panel = panelEl();
    if (!panel) return;
    var host = drawerHost();
    var drawer = host && host.querySelector('[data-cart-drawer][data-jl-inset]');
    var inset = drawer && bagOpen() ? drawer.style.getPropertyValue('--jl-bag-inset') : '';
    if (inset) panel.style.setProperty('--jl-qa-inset', inset);
    else panel.style.removeProperty('--jl-qa-inset');
  }

  /* ---- sheet: open and close ------------------------------------------------ */
  function show(data, source, opener) {
    var sheet = sheetEl();
    if (!sheet || !valid(data)) return false;
    if (st.open) close({ restoreFocus: false, reason: 'other-sheet' });
    st.data = data;
    st.source = source;
    st.opener = opener || null;
    st.inDrawer = !!(opener && opener.closest && opener.closest('cart-drawer'));
    st.busy = false;
    render(data);
    showError('');
    setStatus('');
    sheet.classList.remove('is-open', 'is-dragging');
    sheet.classList.toggle('jl-qa-sheet--drawer', st.inDrawer);
    var panel = panelEl();
    if (panel) panel.style.removeProperty('--jl-qa-drag');
    syncInset();
    sheet.hidden = false;
    st.open = true;
    document.body.classList.add('jl-qa-open');
    inertPage(true);

    // focus moves synchronously inside the click handler (iOS keeps VoiceOver and the keyboard in the sheet)
    var target = sheet.querySelector('.jl-qa-sheet__option[aria-disabled="false"]') ||
      sheet.querySelector('.jl-qa-sheet__option') || focusables()[0];
    focusEl(target);

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (st.open && !sheet.hidden) sheet.classList.add('is-open');
      });
    });
    dispatch('jl:size-sheet-open', { source: source, product_id: data.id, item_name: data.title || '' });
    return true;
  }

  function close(opts) {
    opts = opts || {};
    var sheet = sheetEl();
    if (!st.open || !sheet) return;
    st.open = false;
    st.busy = false;
    sheet.classList.remove('is-open', 'is-dragging');
    var panel = panelEl();
    if (panel) panel.style.removeProperty('--jl-qa-drag');
    sheet.hidden = true;
    sheet.classList.remove('jl-qa-sheet--drawer');
    document.body.classList.remove('jl-qa-open');
    inertPage(false);
    setStatus('');
    var opener = st.opener;
    var data = st.data;
    var source = st.source;
    var inDrawer = st.inDrawer;
    st.opener = null;
    if (opts.restoreFocus !== false) {
      if (!visible(opener) && data) {
        // the opener was re-rendered (cart.js): the same product's trigger in the same place
        var sel = '[data-jl-qa-open][data-product-id="' + cssEsc(data.id) + '"]';
        var scope = inDrawer ? drawerHost() : document;
        opener = scope ? scope.querySelector(sel) : null;
      }
      if (visible(opener) && !opener.closest('[inert]')) {
        focusEl(opener);
      } else if (inDrawer && bagOpen()) {
        var host = drawerHost();
        focusEl(host && host.querySelector('[data-cart-drawer]'));
      }
    }
    dispatch('jl:size-sheet-close', { source: source, reason: opts.reason || 'close' });
  }

  function isOpen() { return st.open; }

  function setBusy(el, on) {
    if (!el || !el.setAttribute) return;
    if (on) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  }

  // one-size products: added at once; a failure opens the sheet with the message so it is seen
  function addOne(data, source, opener) {
    var sz = data.sizes[0] || {};
    if (!sz.id) return Promise.resolve(false);
    setBusy(opener, true);
    return add(sz.id, {
      source: source,
      opener: opener,
      size: sz.v || '',
      productId: data.id,
      itemName: data.title,
      itemBrand: data.brand,
      cents: data.cents,
      currency: data.cur,
      remember: false
    }).then(function (res) {
      setBusy(opener, false);
      if (!res.ok && show(data, source, opener)) {
        var opt = part('.jl-qa-sheet__option');
        if (res.status === 422) markSoldOut(opt, data);
        showError(res.message || str(data, 'error', 'data-error'));
      }
      return false;
    });
  }

  function open(arg) {
    var trigger = null;
    var o = {};
    if (arg && arg.nodeType === 1) {
      trigger = arg;
    } else if (arg && typeof arg === 'object') {
      o = arg;
      trigger = o.opener && o.opener.nodeType === 1 ? o.opener : null;
    }
    if (document.body.classList.contains('jl-sheet-open')) return Promise.resolve(false);
    var source = o.source || (trigger && trigger.getAttribute('data-jl-source')) || 'card_sheet';
    var opener = trigger || (document.activeElement !== document.body ? document.activeElement : null);
    var seq = ++openSeq;

    function go(data) {
      if (!valid(data)) return Promise.resolve(false);
      if (data.one) return addOne(data, source, opener);
      return Promise.resolve(show(data, source, opener));
    }

    // synchronous path when the payload is at hand (focus must move inside the click)
    var data = null;
    if (o.product && typeof o.product === 'object') {
      data = valid(o.product) ? o.product : null;
      return go(data);
    }
    var ih = idHandle(trigger, o.product);
    data = findInline(trigger, ih.id, ih.handle);
    if (data) return go(data);
    if (!ih.handle) return Promise.resolve(false);

    setBusy(trigger, true);
    return fetchData(ih.handle).then(function (d) {
      setBusy(trigger, false);
      if (seq !== openSeq || st.open || document.body.classList.contains('jl-sheet-open')) return false;
      return go(d);
    });
  }

  /* ---- 2) add (K12) ------------------------------------------------------------ */
  function add(variantId, opts) {
    opts = opts || {};
    var id = parseInt(variantId, 10);
    var source = opts.source || 'card_sheet';
    var size = opts.size == null ? '' : String(opts.size);
    if (!id) {
      dispatch('jl:quick-add-error', { source: source, variant_id: 0, size: size, status: 0, message: '' });
      return Promise.resolve({ ok: false, status: 0, message: '' });
    }
    // the opener's payload also says whether this option is a size worth remembering (not a gift card amount)
    var near = (!opts.productId || !opts.itemName || opts.remember == null || opts.itemBrand == null) ? payloadNear(opts.opener) : null;
    var productId = opts.productId || (near && near.id) || 0;
    var itemName = opts.itemName || (near && near.title) || '';
    // phase 5 (K19): the payload's brand name ("Jane Lushka", "LUJÉ" or "") for GA4 item_brand
    var itemBrand = opts.itemBrand != null ? String(opts.itemBrand) : ((near && near.brand) || '');
    var cur = opts.currency || currency(near);
    var remember = opts.remember != null ? !!opts.remember : (near ? near.sw !== false : true);

    var host = drawerHost();
    var fd = new FormData();
    fd.append('id', String(id));
    fd.append('quantity', '1');
    if (host && typeof host.getSectionsToRender === 'function') {
      fd.append('sections', host.getSectionsToRender().map(function (s) { return s.id; }).join(','));
      fd.append('sections_url', window.location.pathname);
    }
    var url = (window.routes && window.routes.cart_add_url) || (root() + 'cart/add');

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: fd
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (json) {
        return { r: r, json: json || {} };
      });
    }).then(function (x) {
      var state = x.json;
      if (!x.r.ok || state.status) {
        var status = parseInt(state.status, 10) || x.r.status || 0;
        var message = state.description || state.message || '';
        if (typeof message !== 'string') message = '';
        dispatch('jl:quick-add-error', { source: source, variant_id: id, size: size, status: status, message: message });
        return { ok: false, status: status, message: message };
      }

      if (remember && size) rememberSize(size);
      var cents = parseInt(state.final_price, 10);
      if (isNaN(cents)) cents = parseInt(state.price, 10);
      if (isNaN(cents)) cents = parseInt(opts.cents, 10) || 0;

      // the sheet closes without sending focus into hidden content; an opener outside the bag gets focus
      // back first, so the bag remembers it as the control to return to when it closes
      if (st.open) close({ restoreFocus: false, reason: 'add' });
      var opener = opts.opener;
      if (visible(opener) && !(opener.closest && opener.closest('cart-drawer')) && !opener.closest('[inert]')) focusEl(opener);

      dispatch('jl:quick-add', {
        source: source,
        product_id: Number(productId || state.product_id || 0),
        variant_id: id,
        size: size,
        item_name: itemName || state.product_title || '',
        item_brand: itemBrand,
        value: cents / 100,
        currency: cur
      });

      var drawer = drawerHost();
      if (drawer && state.sections && typeof drawer.renderContents === 'function') {
        try { drawer.renderContents(state); } catch (e) { /* the bag keeps its old content */ }
        try { drawer.open(); } catch (e) { /* idem */ }
      } else {
        var sheet = sheetEl();
        var cartUrl = (window.routes && window.routes.cart_url) || (sheet && sheet.getAttribute('data-cart-url')) || '/cart';
        window.location.assign(cartUrl);
      }
      return { ok: true, state: state };
    }).catch(function () {
      dispatch('jl:quick-add-error', { source: source, variant_id: id, size: size, status: 0, message: '' });
      return { ok: false, status: 0, message: '' };
    });
  }

  function choose(opt) {
    var data = st.data;
    if (!data || st.busy) return;
    var id = opt.getAttribute('data-variant-id');
    var size = opt.getAttribute('data-value') || '';
    st.busy = true;
    opt.classList.add('is-busy');
    setBusy(opt, true);
    showError('');
    setStatus(str(data, 'adding', 'data-adding'));
    add(id, {
      source: st.source,
      opener: st.opener,
      size: size,
      productId: data.id,
      itemName: data.title,
      itemBrand: data.brand,
      cents: data.cents,
      currency: data.cur,
      remember: !!data.sw
    }).then(function (res) {
      opt.classList.remove('is-busy');
      setBusy(opt, false);
      if (res.ok || st.data !== data) return;
      st.busy = false;
      setStatus('');
      if (!st.open) return;
      if (res.status === 422) markSoldOut(opt, data);
      showError(res.message || str(data, 'error', 'data-error'));
      if (document.activeElement !== opt) focusEl(opt);
    });
  }

  /* ---- drag the handle down to close ------------------------------------------ */
  function bindDrag(sheet) {
    var handle = sheet.querySelector('[data-jl-qa-handle]');
    if (!handle || typeof window.PointerEvent !== 'function') return;
    var startY = null;
    var delta = 0;
    function move(e) {
      if (startY === null) return;
      delta = Math.max(0, e.clientY - startY);
      var panel = panelEl();
      if (panel) panel.style.setProperty('--jl-qa-drag', delta + 'px');
    }
    function end() {
      if (startY === null) return;
      startY = null;
      sheet.classList.remove('is-dragging');
      var panel = panelEl();
      if (panel && delta > Math.min(120, panel.offsetHeight / 3)) {
        close({ reason: 'drag' });
      } else if (panel) {
        panel.style.removeProperty('--jl-qa-drag');
      }
      delta = 0;
    }
    handle.addEventListener('pointerdown', function (e) {
      if (!st.open || (e.button !== undefined && e.button !== 0)) return;
      startY = e.clientY;
      delta = 0;
      sheet.classList.add('is-dragging');
      try { handle.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    });
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  /* ---- 4) bag fallback rows (Shopify related products) ----------------------------- */
  function relatedBlock(productId) {
    var key = root() + '|' + productId;
    if (relatedCache[key]) return relatedCache[key];
    var url = root() + 'recommendations/products?product_id=' + encodeURIComponent(productId) +
      '&limit=6&intent=related&section_id=jl-bag-look';
    var p = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : false; })
      .then(function (html) {
        if (html === false) return false;
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var block = doc.querySelector('[data-jl-bag-look]');
        return block ? block.outerHTML : null;
      })
      .catch(function () { return false; });
    relatedCache[key] = p;
    // a failed request (not ok, network) may be tried again by the next fill; a real "no block" answer stays cached
    p.then(function (h) { if (h === false && relatedCache[key] === p) delete relatedCache[key]; });
    return p;
  }

  function fillFallback() {
    var host = drawerHost();
    if (!host || !bagOpen()) return;
    var ph = host.querySelector('[data-jl-bag-look-fallback]:not([data-jl-state])');
    if (!ph) return;
    var pid = ph.getAttribute('data-product-id');
    if (!pid) { ph.setAttribute('data-jl-state', 'empty'); return; }
    ph.setAttribute('data-jl-state', 'loading');
    relatedBlock(pid).then(function (html) {
      if (!ph.isConnected) { fillFallback(); return; }   // re-rendered meanwhile: fill the new one (cached)
      if (!html) { ph.setAttribute('data-jl-state', 'empty'); return; }
      var exclude = (ph.getAttribute('data-exclude') || '').split(/[\s,|]+/);
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var block = tmp.firstElementChild;
      var rows = block ? block.querySelectorAll('[data-jl-look-row]') : [];
      var kept = 0;
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (kept >= LOOK_ROWS_MAX || exclude.indexOf(row.getAttribute('data-product-id')) !== -1) {
          row.parentNode.removeChild(row);
        } else {
          kept += 1;
          row.setAttribute('data-jl-index', String(kept));
        }
      }
      if (!kept) { ph.setAttribute('data-jl-state', 'empty'); return; }
      ph.setAttribute('data-jl-state', 'done');
      ph.appendChild(block);
    });
  }

  /* ---- 5) empty-bag picks ------------------------------------------------------------ */
  function wakePicks() {
    var host = drawerHost();
    if (!host || !bagOpen()) return;
    var imgs = host.querySelectorAll('[data-jl-bag-picks] img[data-jl-src], [data-jl-bag-picks] img[data-jl-srcset]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var srcset = img.getAttribute('data-jl-srcset');
      var src = img.getAttribute('data-jl-src');
      img.removeAttribute('data-jl-srcset');
      img.removeAttribute('data-jl-src');
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
    }
  }

  function emptyWithoutPicks() {
    var host = drawerHost();
    var block = host && host.querySelector('.jl-bag-empty');
    if (!block || block.querySelector('[data-jl-bag-picks]') || !host.querySelector('cart-items.cart-drawer-is-empty')) return null;
    return block;
  }

  function refetchPicks() {
    if (!emptyWithoutPicks()) return;
    if (!picksReq) {
      picksReq = fetch(root() + '?sections=cart-drawer', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : false; })
        .then(function (json) {
          if (!json) return false;
          var doc = new DOMParser().parseFromString(json['cart-drawer'] || '', 'text/html');
          var picks = doc.querySelector('[data-jl-bag-picks]');
          return picks ? picks.outerHTML : '';
        })
        .catch(function () { return false; });
      // a failed request may be tried again by the next re-render; an answer without picks stays
      picksReq.then(function (h) { if (h === false) picksReq = null; });
    }
    picksReq.then(function (html) {
      var block = html && emptyWithoutPicks();
      if (!block) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var picks = tmp.firstElementChild;
      var cta = block.querySelector('[data-jl-bag-empty-cta]');
      if (cta && cta.parentNode === block) block.insertBefore(picks, cta.nextSibling);
      else block.insertBefore(picks, block.firstChild);
      wakePicks();
    });
  }

  /* ---- wiring ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var trigger = t.closest('[data-jl-qa-open]');
    if (!trigger || inSheet(trigger)) return;
    e.preventDefault();
    if (trigger.disabled || trigger.getAttribute('aria-busy') === 'true') return;
    open(trigger);
  });

  // prefetch the payload of a trigger that has none inline (pointer on it or keyboard focus)
  function maybePrefetch(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var trigger = t.closest('[data-jl-qa-open]');
    if (!trigger || trigger.__jlQaSeen) return;
    trigger.__jlQaSeen = true;
    var ih = idHandle(trigger, null);
    if (!findInline(trigger, ih.id, ih.handle)) prefetch(ih.handle);
  }
  document.addEventListener('pointerover', maybePrefetch, { passive: true });
  document.addEventListener('focusin', maybePrefetch);

  document.addEventListener('keydown', onKeydown, true);

  // focus that leaves the open sheet (something outside the inert set) is pulled back in
  document.addEventListener('focusin', function (e) {
    if (!st.open || inSheet(e.target)) return;
    var f = focusables();
    if (f.length) focusEl(f[0]);
  });

  function bindSheet() {
    var sheet = sheetEl();
    if (!sheet || sheet.__jlQaBound) return;
    sheet.__jlQaBound = true;
    sheet.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var closer = t.closest('[data-jl-qa-close]');
      if (closer) {
        e.preventDefault();
        close({ reason: closer.classList.contains('jl-qa-sheet__backdrop') ? 'backdrop' : 'close' });
        return;
      }
      var opt = t.closest('.jl-qa-sheet__option');
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      choose(opt);
    });
    bindDrag(sheet);
  }

  function onBreakpoint() {
    if (st.open) close({ reason: 'breakpoint' });
  }
  if (mqDesktop) {
    if (typeof mqDesktop.addEventListener === 'function') mqDesktop.addEventListener('change', onBreakpoint);
    else if (typeof mqDesktop.addListener === 'function') mqDesktop.addListener(onBreakpoint);
  }

  // one sheet at a time: the product page's own sheet (phase 3) and the collection filter sheet
  document.addEventListener('jl:pdp-sheet-open', function () { if (st.open) close({ reason: 'other-sheet' }); });
  document.addEventListener('jl:filter-sheet-open', function () { if (st.open) close({ reason: 'other-sheet' }); });

  // K26: a size chosen on the product page is the shopper's size
  document.addEventListener('jl:pdp-state', function (e) {
    var d = e && e.detail;
    var s = d && d.state;
    if (!d || d.reason !== 'change' || !s || s.needsSize || !s.size) return;
    rememberSize(s.size);
  });

  // the bag fallback rows and the empty-bag picks: when the bag opens and whenever its content is re-rendered
  if ('MutationObserver' in window) {
    new MutationObserver(function () {
      if (!bagOpen()) return;
      fillFallback();
      wakePicks();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    var watchHost = function () {
      var host = drawerHost();
      if (!host || host.__jlQaWatched) return;
      host.__jlQaWatched = true;
      new MutationObserver(function () {
        refetchPicks();
        if (!bagOpen()) return;
        fillFallback();
        wakePicks();
      }).observe(host, { childList: true, subtree: true });
    };
    watchHost();
    document.addEventListener('shopify:section:load', watchHost);
  }

  bindSheet();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindSheet);
  fillFallback();

  window.JLSizeSheet = {
    __v: 1,
    open: open,
    close: function (opts) { close(opts || {}); },
    isOpen: isOpen,
    getData: getData,
    prefetch: prefetch,
    add: add,
    rememberSize: rememberSize,
    rememberedSize: rememberedSize
  };
})();
